import { UserView } from '@/pages/Screener';
import {
  SavedScreenerButton,
  SavedScreenerButtonContent,
  StyledEmbla,
} from './styled';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid } from '@simplywallst/ui-core';
import Carousel, { CarouselRef } from '@/components/Carousel';
import { useTracking } from 'react-tracking';

interface Props {
  userViews: UserView[];
  selectedView: string;
  onUserViewChange: (id: string, name: string) => void;
}

const SavedScreenersSlider = ({
  userViews,
  selectedView,
  onUserViewChange,
}: Props) => {
  const sortedUserViews = useMemo(
    () =>
      [...userViews].sort((a, b) => (b.modifyDate ?? 0) - (a.modifyDate ?? 0)),
    [userViews]
  );

  const carouselRef = useRef<CarouselRef>(null);

  const visitedIndex = sortedUserViews.findIndex(
    (userView) => userView.id === selectedView
  );
  const [selectedIndex, setSelectedIndex] = useState(visitedIndex);

  useEffect(() => {
    if (typeof selectedIndex === 'number') return;

    setSelectedIndex(visitedIndex);
  }, [visitedIndex, selectedIndex]);

  const onScroll = useCallback(() => {
    if (!carouselRef.current) return;
    const currentScrollSnap = carouselRef.current.selectedScrollSnap();
    if (typeof currentScrollSnap !== 'number') return;

    setSelectedIndex(currentScrollSnap);
  }, []);

  const { trackEvent } = useTracking();

  return (
    <StyledEmbla>
      <Carousel
        ref={carouselRef}
        align="start"
        totalSlides={sortedUserViews.length}
        containScroll="trimSnaps"
        speed={100}
        startIndex={selectedIndex}
        inViewThreshold={1}
        onScroll={onScroll}
        showArrows
      >
        <Grid autoFlow="column" autoColumns="150px" gap="4px">
          {sortedUserViews.map((userView, index) => (
            <SavedScreenerButton
              key={userView.id}
              onClick={() => {
                onUserViewChange(userView.id, userView.name);
                trackEvent({
                  type: 'track',
                  subject: 'my_saved_screeners',
                  action: 'click',
                  option: userView.id,
                  index,
                });
              }}
              isActive={selectedView === userView.id}
            >
              <SavedScreenerButtonContent>
                {userView.name}
              </SavedScreenerButtonContent>
            </SavedScreenerButton>
          ))}
        </Grid>
      </Carousel>
    </StyledEmbla>
  );
};

export default SavedScreenersSlider;
